import DeleteModal from '@/components/atoms/DeleteModal';
import InfoText from '@/components/atoms/InfoText';
import { openModal, closeModal, useModal } from '@/store/slices/Modals';
import TitleText from '@/components/atoms/TitleText';
import { ConfigProvider, Table } from 'antd';
import classNames from 'classnames';
import { CloseCircle, Edit2 } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

export default function SpecsTable({ preview = false }: { preview?: boolean }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tableActionsOpen, setTableActionsOpen] = useState<null | number>();

  const { modals } = useModal();

  const formContext = useFormContext();
  const specsValue = formContext.watch('specs');

  const { remove } = useFieldArray({
    control: formContext.control,
    name: 'specs',
  });

  const columns = [
    {
      title: <InfoText text={t('biddingPost.specType')} className="!font-normal !text-light-text-primary dark:!text-dark-text-primary" size="sm" />,
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: t('biddingPost.specValue'),
      dataIndex: 'value',
      key: 'value',
    },
  ];

  const handleDelete = () => {
    remove(Number(modals?.['DELETE_SPEC']?.data?.index));
  };

  return (
    <div data-testid="bidding-post-specs-table">
      {/* Desktop View - Unchanged */}
      <div className="hidden md:block">
        <ConfigProvider
          theme={{
            components: {
              Table: {
                colorBgContainer: '#fff',
              },
            },
          }}
        >
          <Table
            // @ts-expect-error
            dataSource={specsValue?.map((spec, idx) => ({
              key: idx,
              type: spec.type,
              value: (
                <div className="relative flex items-center w-full min-h-[24px]" data-testid={`bidding-post-specs-table-row-${idx}`}>
                  <span
                    className="block w-full ltr:pr-16 rtl:pl-16 truncate"
                    data-testid={`bidding-post-specs-table-value-${idx}`}
                  >
                    {spec.value}
                  </span>

                  <div
                    className={classNames(
                      `absolute top-1/2 -translate-y-1/2 flex gap-3 transition-all duration-300 ease-out rtl:left-0 ltr:right-0 z-10`,
                      tableActionsOpen === idx
                        ? 'opacity-100 translate-x-0'
                        : 'opacity-0 translate-x-2 pointer-events-none'
                    )}
                    data-testid={`bidding-post-specs-table-actions-${idx}`}
                  >
                    <button
                      type="button"
                      className="cursor-pointer hover:opacity-70 transition-all duration-300 ease-in-out"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(
                          openModal({
                            id: 'SPECS_MODAL',
                            data: { ...spec, index: idx },
                          })
                        );
                      }}
                    >
                      <Edit2
                        size="18"
                        variant="Bold"
                        className="!text-light-information"
                      />
                    </button>

                    <button
                      type="button"
                      className="cursor-pointer hover:opacity-70 transition-all duration-300 ease-in-out"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(
                          openModal({
                            id: 'DELETE_SPEC',
                            data: { index: idx },
                          })
                        );
                      }}
                    >
                      <CloseCircle
                        size="18"
                        variant="Bold"
                        className="!text-light-warning"
                      />
                    </button>
                  </div>
                </div>
              ),
            }))}
            columns={columns}
            pagination={false}
            onRow={(record: { key: number }) => {
              if (preview) return {};
              else
                return {
                  onMouseEnter: () => {
                    setTableActionsOpen(record?.key);
                  },
                  onMouseLeave: () => {
                    setTableActionsOpen(null);
                  },
                };
            }}
          />
        </ConfigProvider>
      </div>

      {/* Mobile View - Matches Figma */}
      <div className="block md:hidden flex-col gap-2 mt-4">
        {specsValue && specsValue.length > 0 && (
          <div className="flex justify-between items-center px-4 py-2 mb-2 border-b border-gray-100 dark:border-gray-800">
            <InfoText text={t('biddingPost.specType')} className="!font-normal !text-neutral-600 dark:!text-dark-text-secondary" size="sm" />
            <TitleText text={t('biddingPost.specValue')} className="!font-bold text-neutral-900 dark:text-dark-text-primary" size="sm" />
          </div>
        )}

        <div className="flex flex-col gap-2">
          {specsValue?.map((spec: { type: string; value: string }, idx: number) => (
            <div
              key={idx}
              className={classNames(
                "flex justify-between items-center px-4 py-3 rounded-xl transition-colors cursor-pointer",
                idx % 2 !== 0 ? "bg-[#FAFAFA] dark:bg-gray-800" : "bg-transparent"
              )}
              onMouseEnter={() => {
                if (!preview) setTableActionsOpen(idx);
              }}
              onMouseLeave={() => {
                if (!preview) setTableActionsOpen(null);
              }}
              onClick={() => {
                if (!preview) {
                  setTableActionsOpen(tableActionsOpen === idx ? null : idx);
                }
              }}
              data-testid={`bidding-post-specs-mobile-row-${idx}`}
            >
              <span className="text-neutral-700 dark:text-neutral-300 text-sm truncate flex-1 pr-2">
                {spec.type}
              </span>
              
              <div className="flex items-center justify-end">
                <span className="font-bold text-neutral-900 dark:text-white text-base truncate transition-all duration-300">
                  {spec.value}
                </span>
                
                {!preview && (
                  <div 
                    className={classNames(
                      "flex items-center gap-2 overflow-hidden transition-all duration-300 ease-out",
                      tableActionsOpen === idx ? "opacity-100 max-w-[100px] ml-4" : "opacity-0 max-w-0 ml-0 pointer-events-none"
                    )}
                  >
                    <button
                      type="button"
                      className="cursor-pointer hover:opacity-70 transition-opacity p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(openModal({ id: 'SPECS_MODAL', data: { ...spec, index: idx } }));
                      }}
                    >
                      <Edit2 size="18" variant="Bold" className="!text-light-information" />
                    </button>
                    <button
                      type="button"
                      className="cursor-pointer hover:opacity-70 transition-opacity p-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(openModal({ id: 'DELETE_SPEC', data: { index: idx } }));
                      }}
                    >
                      <CloseCircle size="18" variant="Bold" className="!text-light-warning" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {modals?.['DELETE_SPEC']?.isOpen && (
        <div data-testid="bidding-post-specs-delete-modal">
          <DeleteModal
            isOpen={modals?.['DELETE_SPEC']?.isOpen}
            onClose={() => dispatch(closeModal({ id: 'DELETE_SPEC' }))}
            onDelete={() => {
              handleDelete?.();
              dispatch(closeModal({ id: 'DELETE_SPEC' }));
            }}
          />
        </div>
      )}
    </div>
  );
}
